import React, { useEffect } from "react"
import { useHistory } from "react-router-dom"
import { gql, useMutation, useQuery } from "@apollo/client"

import { Layout, Fieldset } from "components"

import { Formik, Form, Field } from "formik"
import * as yup from "yup"

import TripGeneralForm from "components/Common/Forms/TripGeneral"

import cn from "classnames"

import { getTripEditModalProps } from "utils/mappers"

import { useSteps } from "hooks/useSteps"

const CREATE_TRIP = gql`
  mutation CreateTrip($input: CreateTripInput!) {
    createTrip(input: $input) {
      id
      name
      url
      code
      startDate
      endDate
      photoURL
      image {
        url
      }

      agents {
        displayName
      }
      leaders {
        displayName
      }
    }
  }
`

const createTripSchema = yup.object().shape({
  name: yup.string().required("Name is required"),
  code: yup.number().required("Code is required"),
  startDate: yup.date().required("Start date is required"),
  endDate: yup.date().required("End date is required"),
  // destinations: yup
  //   .array("You must have at least one destination")
  //   .required("You must have at least one destination")
  //   .min(1, "You must have at least one destination")
  //   .nullable(),

  image: yup.object().required("Cover image is required").nullable(),

  legal: yup.object().shape({
    contract: yup.object().required("Contract is required").nullable(),
  }),

  account: yup.object().required("Account is required").nullable(),

  agency: yup.object().required("Agency is required").nullable(),

  // agents: yup
  //   .array("You must have at least one agent")
  //   .required("You must have at least one agent")
  //   .min(1, "You must have at least one agent")
  //   .nullable(),

  payment: yup.object().shape({
    price: yup
      .number()
      .nullable()
      .required("Base price is required")
      .min(0, "Based price can't be 0"),

    deposits: yup
      .array()
      .of(
        yup.object({
          amount: yup
            .number()
            .typeError("Amount must be a number")
            .nullable()
            .when("$index", (_, schema, { index, parent }) => {
              return parent.amount
                ? schema
                    .min(1, "Amount more then 0")
                    .required("Amount is required")
                : schema
            }),
          dueDate: yup
            .date()
            .when("$index", (_, schema, { index }) => {
              return index === 0
                ? schema
                : schema.required("Due date is required")
            })
            .nullable(),
        }),
      )
      .test(
        "sales-sum-test",
        "Deposit sum is more or equal to base price",
        function (deposits, { parent }) {
          const total = parent?.price
          const sum = deposits.reduce(
            (sum, item) => (sum += item.amount ? item.amount : 0),
            0,
          )

          return total >= sum
        },
      ),
  }),
})

const mockTrip = {
  name: "Wonderland",
  code: 1111,
  startDate: "2021-10-10",
  endDate: "2021-10-20",
  // location: "Scandinavia",
  image: {
    id: "19d26db8-16ee-4a9f-995c-fd17df67312f",
    url: "https://firebasestorage.googleapis.com/v0/b/grouptravelres.appspot.com/o/img%2F11-gaddafi-rusli-2ueUnL4CkV8-unsplash.jpg?alt=media&token=9677030a-5c93-4318-a1b0-94ea9ab0dd05",
    name: "gaddafi-rusli-2ueUnL4CkV8-unsplash.jpg",
    tags: null,
  },
  legal: {
    contract: {
      label: "Cartagena 2021",
      value: {
        id: "7c4e94405c0bbcff9592b46f8a2f0549087269e3",
        name: "Cartagena 2021",
      },
    },
  },

  agency: { label: "Alpine", value: { id: 1, name: "Alpine" } },
  agents: [
    {
      key: "2b44a158-f60a-4c93-929a-45403ec07ca5",
      label: "Arsenije Savic",
      value: {
        id: "2b44a158-f60a-4c93-929a-45403ec07ca5",
        displayName: "Arsenije Savic",
        email: "arsenijesavic@me.com",
      },
    },
  ],
  leaders: [
    {
      key: "2b44a158-f60a-4c93-929a-45403ec07ca5",
      label: "Arsenije Savic",
      value: {
        id: "2b44a158-f60a-4c93-929a-45403ec07ca5",
        displayName: "Arsenije Savic",
        email: "arsenijesavic@me.com",
      },
    },
  ],
  payment: {
    price: 1000,
    deposits: [
      { amount: 100 },
      { amount: 800, dueDate: "2021-06-30" },
      { amount: 0, dueDate: "2021-06-30" },
    ],
  },
}

const GroupsCreatePage = () => {
  const history = useHistory()
  const [createTrip] = useMutation(CREATE_TRIP)

  // const { activeIndex, step, prevStep, nextStep, isLastStep } = useSteps([
  //   "CONFIGURE_TRIP",
  //   "CREATE_TRIP",
  // ])

  // const stepTitle = {
  //   CONFIGURE_TRIP: "Configure",
  //   CREATE_TRIP: "General",
  // }

  const formikProps = {
    initialValues: {
      name: "",
      code: "",
      startDate: "",
      endDate: "",
      destinations: null,
      image: null,

      legal: {
        contract: null,
      },

      account: { label: "Alpine Adventures", value: "ALPINE_ADVENTURES" },
      agency: null,
      agents: null,
      leaders: null,

      payment: {
        price: null,
        deposits: [
          { amount: null }, //'First deposit
          {
            // amount: 0,
            dueDate: "",
          }, //Final payment
        ],
      },

      // ...mockTrip,
    },
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema: createTripSchema,
    onSubmit: async ({ template, ...values }, actions) => {
      try {
        const input = {
          ...values,
          type: "GROUP",

          destinations: values?.destinations?.map((_) => _.value),
          image: {
            id: values?.image?.id,
            name: values?.image?.name,
            url: values?.image?.url,
          },

          payment: {
            ...values.payment,
            deposits: values?.payment?.deposits.map((part) => {
              const { id, ...rest } = part
              return rest
            }),
          },
          legal: {
            ...values.legal,
            contract: { ...values.legal.contract.value },
          },

          account: values?.account?.value,
          agency: { ...values?.agency?.value },
          agents: values?.agents?.map((_) => _?.value),
          leaders: values?.leaders?.map((_) => _?.value),
        }

        const { data } = await createTrip({ variables: { input } })

        const { id } = data.createTrip

        history.push(`/groups/${id}`, { action: "CREATE_TRIP", id, ...input })
      } catch (e) {
        const errors = e.graphQLErrors.map((_) => _)
        actions.setFieldError("server", errors)
      }
    },
  }

  return (
    <Layout title="Create Group">
      <header className="py-8 bg-gray-800 text-gray-100">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
          <h1 className="font-black text-3xl">Create Group</h1>
        </div>
      </header>

      <main className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 pb-32">
        <div className="relative -mt-24 z-30 flex items-start space-x-4">
          <div className="w-8/12 bg-white rounded-lg shadow-lg">
            <header className="p-6 border-b-2 border-gray-100 flex justify-between items-center">
              <h3 className="text-xl leading-none font-medium text-gray-900">
                Create Group
              </h3>
            </header>

            <Formik {...formikProps}>
              {({ values, errors, isSubmitting, setValues }) => {
                return (
                  <Form className="p-6">
                    <Template
                      template={values?.template}
                      onTemplate={(data) => setValues({ ...values, ...data })}
                    />

                    <TripGeneralForm values={values} />

                    <div className="py-3">
                      {errors?.server?.map(({ code, message }) => (
                        <div role="alert">
                          <div className="bg-red-500 text-white font-bold rounded-t px-4 py-2">
                            {code}
                          </div>
                          <div className="border border-t-0 border-red-400 rounded-b bg-red-100 px-4 py-3 text-red-700">
                            <p>{message}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    <FormNavigation isSubmitting={isSubmitting} />
                  </Form>
                )
              }}
            </Formik>
          </div>
        </div>
      </main>
    </Layout>
  )
}

export default GroupsCreatePage

const Template = ({ template, onTemplate }) => {
  // What type of trip do you want to create?

  const GROUPS = gql`
    query Trips(
      $filter: TripFilterInput
      $page: Int
      $size: Int
      $sort: TripSortInput
    ) {
      trips(filter: $filter, page: $page, size: $size, sort: $sort) {
        name
        # code
        type
        # startDate
        # endDate
        destinations
        image {
          id
          url
        }

        legal {
          contract
        }

        payment {
          price
          deposits {
            amount
            dueDate
          }
        }

        agency {
          id
          name
        }

        agents {
          id
          displayName
          email
        }

        leaders {
          id
          displayName
          email
        }
      }
    }
  `

  const variables = {
    page: 0,
    size: 10,
    // filter: { roles: ["ADMIN", "AGENT", "LEADER"] },
    // sort: { displayName: "ASC" },
    filter: { type: { eq: "GROUP" } },
  }

  const { data, loading, fetchMore } = useQuery(GROUPS, {
    variables,
    notifyOnNetworkStatusChange: true,
  })

  const groups = data?.trips?.map((group) => ({
    key: group.id,
    label: group.name,
    value: { ...group },
  }))

  const handleSearch = (_search, e) => {
    if (e.action === "input-change") {
      const search = _search?.toLowerCase()
      fetchMore({
        // variables: { ...variables, filter: { ...variables.filter, search } },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev
          return Object.assign({}, prev, {
            // trips: uniqBy(
            //   [...prev.trips, ...fetchMoreResult.trips],
            //   "displayName",
            // ),
          })
        },
      })
    }
  }

  useEffect(() => {
    if (template?.value) {
      onTemplate(getTripEditModalProps(template.value))
    }
  }, [template])

  return (
    <>
      <div className="px-2 py-3 mt-2">
        <Fieldset
          name="template"
          label="Trip template *"
          component="select"
          options={groups}
          cacheOptions
          // isMulti
          isLoading={loading}
          onInputChange={handleSearch}
          getOptionValue={(opt) => opt.key}
          mapping={{ label: "displayName" }}
          placeholder="Type to search"
        />
      </div>

      <div className="my-10 flex items-center space-x-4 w-full">
        <p>Or</p>
        <div className="w-full border-t-2 border-gray-200" />
      </div>
    </>
  )
}

const FormNavigation = ({ isSubmitting }) => {
  return (
    <nav className="mt-6 py-3 sm:flex sm:flex-row-reverse">
      <span className="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
        <button
          disabled={isSubmitting}
          type="submit"
          className="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-green-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-green-500 focus:outline-none focus:border-green-700 focus:shadow-outline-green transition ease-in-out duration-150 sm:text-sm sm:leading-5 disabled:opacity-50 disabled:cursor-wait"
        >
          Next
        </button>
      </span>

      {/* {showBack && (
        <span className="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
          <button
            onClick={goBack}
            type="button"
            className="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition ease-in-out duration-150 sm:text-sm sm:leading-5"
          >
            Back
          </button>
        </span>
      )} */}
    </nav>
  )
}
